import { useEffect, useState } from "react";
import {
  RecordContextProvider,
  ResourceContextProvider,
  useGetIdentity,
  type Identifier,
  useInfiniteGetList,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Task as TaskRow } from "@/components/atomic-crm/tasks/Task";
import type { Task } from "@/components/atomic-crm/types";
import { taskBucket } from "./workflow";

const groups = [
  ["overdue", "Scadute"],
  ["today", "Oggi"],
  ["blocked", "In attesa"],
  ["upcoming", "In programma"],
  ["undated", "Data da verificare"],
] as const;

export const NarsTaskList = ({
  contactId,
}: { contactId?: Identifier } = {}) => {
  const { identity, isPending: identityPending } = useGetIdentity();
  const [scope, setScope] = useState("mine");
  const [kind, setKind] = useState("all");
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const {
    data,
    isPending,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "due_date", order: "ASC" },
      filter: {
        "done_date@is": null,
        ...(contactId != null
          ? { contact_id: contactId }
          : scope === "mine"
            ? { sales_id: identity?.id }
            : {}),
        ...(kind === "follow-up" ? { type: "follow-up" } : {}),
      },
    },
    { enabled: identity?.id != null },
  );
  const tasks =
    data?.pages
      .flatMap((page) => page.data)
      .filter((task) => task.done_date == null) ?? [];
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        {contactId == null && (
          <div
            className="inline-flex gap-1 rounded-md border border-border p-1"
            aria-label="Attività da mostrare"
          >
            <Button
              size="sm"
              variant={scope === "mine" ? "secondary" : "ghost"}
              aria-pressed={scope === "mine"}
              onClick={() => setScope("mine")}
            >
              Le mie
            </Button>
            <Button
              size="sm"
              variant={scope === "studio" ? "secondary" : "ghost"}
              aria-pressed={scope === "studio"}
              onClick={() => setScope("studio")}
            >
              Studio
            </Button>
          </div>
        )}
        <label className="text-sm">
          <span className="sr-only">Tipo di attività</span>
          <select
            aria-label="Tipo di attività"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3"
          >
            <option value="all">Tutte le attività aperte</option>
            <option value="follow-up">Solo ricontatti</option>
          </select>
        </label>
      </div>
      {identityPending || (isPending && identity) ? (
        <p role="status">Caricamento attività…</p>
      ) : !identity ? (
        <p role="alert">Accedi per vedere le attività.</p>
      ) : error ? (
        <div role="alert" className="space-y-3">
          <p>Non riesco a caricare le attività.</p>
          <Button variant="outline" onClick={() => refetch()}>
            Riprova
          </Button>
        </div>
      ) : (
        <>
          {!tasks.length && (
            <p className="text-sm text-muted-foreground">
              {contactId != null
                ? "Nessun prossimo passo programmato."
                : scope === "mine"
                  ? "Nessuna attività aperta assegnata a te."
                  : "Nessuna attività aperta nello studio."}
            </p>
          )}
          <ResourceContextProvider value="tasks">
            {groups.map(([key, label]) => {
              const items = tasks.filter(
                (task) => taskBucket(task, now) === key,
              );
              return items.length ? (
                <section key={key} aria-label={label} className="space-y-4">
                  <h3 className="text-sm font-semibold">
                    {label}{" "}
                    <span className="font-normal text-muted-foreground">
                      ({items.length})
                    </span>
                  </h3>
                  {items.map((task) => (
                    <RecordContextProvider key={task.id} value={task}>
                      <div className="space-y-1 border-b border-border pb-4">
                        <TaskRow task={task} showContact={contactId == null} />
                      </div>
                    </RecordContextProvider>
                  ))}
                </section>
              ) : null;
            })}
          </ResourceContextProvider>
          {hasNextPage && (
            <Button
              variant="outline"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "Caricamento…" : "Carica altre attività"}
            </Button>
          )}
        </>
      )}
    </div>
  );
};
